import { effect, inject, Injectable, signal } from '@angular/core';
import { ApiDataAccess, getValue, Query, QueryBuilder } from '@softline/core';
import { DateRange } from '@softline/ui-core';
import { SalesStatisticsGridDataSource } from '../grid/sales-statistics-grid.data-source';
import { applyActiveFilters } from '../helpers/apply-active-filters.function';
import {
  addDateFilter,
  addDbPercentColumn,
  addPeriodColumns,
  addPeriodFilters,
  addValueColumns,
  splitByPeriod,
  valFromRow,
} from '../helpers/document-type-query.helpers';
import {
  InventoryValues,
  loadInventoryValues,
  resolveInventoryValues,
} from '../helpers/inventory-values';
import { ApiRow } from '../types/api-row';
import { WarehouseInventoryRow } from '../types/chart-row';
import { SalesStatisticsQueryService } from './sales-statistics-query.service';

interface LoadResult {
  data: ApiRow[];
}

@Injectable()
export class WarehouseInventoryChartService {
  queryService = inject(SalesStatisticsQueryService);
  private dataAccess = inject(ApiDataAccess);

  private cache = new Map<string, WarehouseInventoryRow[]>();
  /** Lagerwert per warehouse, keyed like `cache` — separate query, own cache. */
  private inventoryCache = new Map<string, Promise<Map<string, InventoryValues>>>();

  readonly warehouseRows = signal<WarehouseInventoryRow[]>([]);
  readonly loading = signal(false);
  readonly hasError = signal(false);
  lastParamsKey = '';

  constructor() {
    let lastInvalidationKey: string | null = null;
    effect(() => {
      const key = JSON.stringify([
        this.queryService.activeFilters(),
        this.queryService.dateRange(),
        this.queryService.referencePeriod(),
        this.queryService.documentType(),
        this.queryService.statisticType(),
        this.queryService.refreshTick(),
      ]);
      if (lastInvalidationKey === null) {
        lastInvalidationKey = key;
        return;
      }
      if (lastInvalidationKey === key) return;
      lastInvalidationKey = key;
      this.cache.clear();
      this.inventoryCache.clear();
      this.lastParamsKey = '';
    });
  }

  async loadWarehouseInventory(
    period: DateRange,
    referencePeriod: DateRange | null,
    documentType = 'INVOICE',
    statisticType = 'TOTAL',
    signal?: AbortSignal
  ): Promise<WarehouseInventoryRow[]> {
    const cacheKey = JSON.stringify({
      period,
      referencePeriod,
      documentType,
      statisticType,
    });
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    const ds = new SalesStatisticsGridDataSource(
      this.dataAccess,
      documentType,
      statisticType
    );

    const base = new QueryBuilder<ApiRow>()
      .start(0)
      .end(2000)
      .column('warehouse.warehouse', { usage: 'group' })
      .column('warehouse.name', { usage: 'group' });
    addValueColumns(base);
    addDbPercentColumn(base);
    base.sort('warehouse.warehouse', 'ASC');
    applyActiveFilters(base, this.queryService);

    let currentData: ApiRow[];
    let refData: ApiRow[];

    if (referencePeriod) {
      const builder = base.copy();
      addPeriodColumns(builder);
      addPeriodFilters(builder, period, referencePeriod);
      const result = (await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal
      )) as LoadResult;
      const split = splitByPeriod(result?.data ?? []);
      currentData = split.current;
      refData = split.previous;
    } else {
      const builder = base.copy();
      addDateFilter(builder, period.from, period.to);
      const result = (await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal
      )) as LoadResult;
      currentData = result?.data ?? [];
      refData = [];
    }

    const keyOf = (r: ApiRow): string => String(getValue(r, 'warehouse.warehouse') ?? '');
    const currentByKey = new Map(currentData.map((r) => [keyOf(r), r]));
    const refByKey = new Map(refData.map((r) => [keyOf(r), r]));
    const allKeys = [...new Set([...currentByKey.keys(), ...refByKey.keys()])];

    const rows: WarehouseInventoryRow[] = allKeys
      .map((key) => {
        const o = currentByKey.get(key);
        const ref = refByKey.get(key);
        const src = o ?? ref;
        const code = getValue(src, 'warehouse.warehouse');
        const name = getValue(src, 'warehouse.name');
        const field = name && name !== code ? `${code} - ${name}` : (code ?? name ?? '');
        // Filled in below from the separate Lagerwert query.
        const inventoryValue = 0;
        const referenceInventoryValue = 0;
        const revenue = valFromRow(o, 'net');
        const referenceRevenue = referencePeriod ? valFromRow(ref, 'net') : revenue;
        const db = valFromRow(o, 'contribution_margin');
        const referenceDb = referencePeriod ? valFromRow(ref, 'contribution_margin') : db;
        const dbPercent = valFromRow(o, 'contribution_margin_percent');
        const referenceDbPercent = referencePeriod
          ? valFromRow(ref, 'contribution_margin_percent')
          : dbPercent;
        return {
          field,
          code,
          inventoryValue,
          referenceInventoryValue,
          minInventory: Math.min(inventoryValue, referenceInventoryValue),
          posDelta:
            referenceInventoryValue < inventoryValue
              ? inventoryValue - referenceInventoryValue
              : 0,
          negDelta:
            referenceInventoryValue > inventoryValue
              ? referenceInventoryValue - inventoryValue
              : 0,
          revenue,
          referenceRevenue,
          minRevenue: Math.min(revenue, referenceRevenue),
          revenueDeltaPos: referenceRevenue < revenue ? revenue - referenceRevenue : 0,
          revenueDeltaNeg: referenceRevenue > revenue ? referenceRevenue - revenue : 0,
          db,
          referenceDb,
          minDb: Math.min(db, referenceDb),
          dbDeltaPos: referenceDb < db ? db - referenceDb : 0,
          dbDeltaNeg: referenceDb > db ? referenceDb - db : 0,
          dbPercent,
          referenceDbPercent,
          minDbPercent: Math.min(dbPercent, referenceDbPercent),
          dbPercentDeltaPos:
            referenceDbPercent < dbPercent ? dbPercent - referenceDbPercent : 0,
          dbPercentDeltaNeg:
            referenceDbPercent > dbPercent ? referenceDbPercent - dbPercent : 0,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
    this.cache.set(cacheKey, rows);
    return this.applyInventory(rows, ds, cacheKey, period, referencePeriod);
  }

  /** The Lagerwert rides in its own narrow query here too: this chart also plots
      Umsatz/DB/DB%, and bundling the Lagerwert measure would put those on the
      backend's combined path — different numbers than every other surface. */
  private async applyInventory(
    rows: WarehouseInventoryRow[],
    ds: SalesStatisticsGridDataSource,
    cacheKey: string,
    period: DateRange,
    referencePeriod: DateRange | null
  ): Promise<WarehouseInventoryRow[]> {
    const keys = [...new Set(rows.map((row) => String(row.code ?? '')))].filter(Boolean);
    if (keys.length === 0) return rows;
    const inventoryBase = new QueryBuilder<ApiRow>()
      .start(0)
      .end(keys.length * 2 + 10)
      .column('warehouse.warehouse', { usage: 'group' });
    applyActiveFilters(inventoryBase, this.queryService);

    const values = await resolveInventoryValues(this.inventoryCache, cacheKey, () =>
      loadInventoryValues(
        ds,
        inventoryBase,
        'warehouse.warehouse',
        period,
        referencePeriod,
        { keys }
      )
    );
    if (!values) return rows;

    return rows
      .map((row) => {
        const entry = values.get(String(row.code ?? ''));
        const inventoryValue = entry?.value ?? 0;
        const referenceInventoryValue = entry?.reference ?? 0;
        return {
          ...row,
          inventoryValue,
          referenceInventoryValue,
          minInventory: Math.min(inventoryValue, referenceInventoryValue),
          posDelta:
            referenceInventoryValue < inventoryValue
              ? inventoryValue - referenceInventoryValue
              : 0,
          negDelta:
            referenceInventoryValue > inventoryValue
              ? referenceInventoryValue - inventoryValue
              : 0,
        };
      })
      .filter(
        (r) =>
          r.inventoryValue !== 0 || r.referenceInventoryValue !== 0 || r.revenue !== 0
      )
      .sort((a, b) => b.inventoryValue - a.inventoryValue);
  }
}
